import React, { useState, useEffect } from "react";
import * as Utils from "../../../commons/utils/Utils.js";
import Security from "../../../commons/security/Security.js";
import { Redirect } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import MovilesInmovilizadosDashboard from "./MovilesInmovilizadosDashboard.jsx";
import { StyledTableCell, StyledTooltip } from "../../../commons/utils/TableStyles.js";
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import UpIcon from '@material-ui/icons/KeyboardArrowUp';
import { Table, TableBody, TableContainer, TableHead, TablePagination, TableRow } from "@material-ui/core";
import MovilesInmovilizadosTableHeader from "./MovilesInmovilizadosTableHeader.jsx";
import MovilesInmovilizadosRow from "./MovilesInmovilizadosRow.jsx";
import MovilesInmovilizadosGridFiltros from "./MovilesInmovilizadosFiltros.jsx";
import GridSearchResultsBadge from "../../../commons/filters/GridSearchResultsBadge.jsx";
import GridColumnSelector from "../../../commons/grid/GridColumnSelector.jsx";
import { GridToolbarButton } from "../../../commons/grid/GridToolbar.jsx";
import LimpiarFiltros from "../../../assets/images/limpiar-filtros.svg";
import { siguienteOrden } from "../../../commons/grid/useGridState.js";
import useColumnVisibility from "../../../commons/grid/useColumnVisibility.js";
import { getMovilesInmovilizadosFilterDefaults } from "./movilesInmovilizadosFilterFields.js";
import {
    MOVILES_INMOVILIZADOS_COLUMNAS_STORAGE_KEY,
    buildMovilesInmovilizadosColumns,
} from "./movilesInmovilizadosColumns.js";

const PAGINATION_INIT = {
	page: 0,
	perPage: 10,
}

const FILTER_QUERY_INIT = {
	tipoDeTicket: '',
    base: '',
    region: '',
    subregion: '',
    servicio: '',
    gerenciador: '',
    abono: '',
    marca: '',
    modelo: '',
    anio: '',
    tipoVehiculo: '',
    funcion: '',
    movilEstado: '',
    fechaDesde: '',
    fechaHasta: ''
}

const MovilesInmovilizadosGrid = () => {
    const intl = useIntl();
    const [inmovilizados, setInmovilizados] = useState([]);
    const [totales, setTotales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [redirectTo, setRedirectTo] = useState(null);
    const [dropdownActive, setDropdownActive] = useState(false);
    const [filterResetInputs, setFilterResetInputs] = useState(false);

    const [pagination, setPagination] = useState({ ...PAGINATION_INIT });
    const [count, setCount] = useState(0);
    const [searchActive, setSearchActive] = useState(false);

    // Qué columnas eligió ver el usuario. El hook persiste las OCULTAS por nombre —acotado al
    // usuario, como los filtros— y la grilla usa `esVisible` en el thead y en cada fila.
    const columnas = useColumnVisibility(
        buildMovilesInmovilizadosColumns(),
        MOVILES_INMOVILIZADOS_COLUMNAS_STORAGE_KEY
    );
    // Cuántas se ven: es el colSpan de la fila de detalle y el de las filas de estado. El hook
    // no lo expone, y se deriva de su mapa en una línea.
    const cantidadVisible = Object.keys(columnas.visibles).filter((name) => columnas.visibles[name]).length;

    // Ordenamiento. Se usa `siguienteOrden` de `useGridState` —la misma regla de ciclo que el
    // resto de las grillas— pero no el hook entero: ese además maneja la paginación, que acá ya
    // la lleva `pagination`. El default es el ORDER BY que el reporte tenía hardcodeado.
    const [sort, setSort] = useState({ campo: 'base', direccion: 'asc' });

    const requestSort = (campo) => {
        setSort((prev) => siguienteOrden(prev, campo));
        setPagination((prev) => ({ ...prev, page: 0 }));
    };

    // VEC-3696: la persistencia es del panel compartido y es INCONDICIONAL. Se eliminó el
    // switch "Persistir Filtros" (Scenario 02) y las dos claves propias que manejaba esta
    // grilla (`inmovilizadosFilters` y `persistirInmovilizadosFilters`).
    //
    // Arranca en `null` A PROPÓSITO: con `applyOnMount` el panel entrega su estado —que puede
    // venir de localStorage— apenas monta, y el efecto de abajo espera ese aviso. Si arrancara
    // con los defaults, saldría una consulta sin filtrar que se descartaría al instante.
    const [filters, setFilters] = useState(null);

    useEffect(() => {
        setLoading(true);
        if (Security.hasPermission('REPORTES_MOVILES_INMOVILIZADOS')) {
            getTotales();
        } else {
            setRedirectTo('/error');
        }
    }, []);

    // La consulta sale cuando el panel ya dijo con qué filtros (`filters !== null`), y cada vez
    // que cambian los filtros o la paginación.
    useEffect(() => {
        if (filters === null) return;
        setLoading(true);
        fetchGridData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination, filters, sort]);

    const getTotales = () => {
        setLoading(true);
        Utils.getData(`/moviles-inmovilizados/totales`)
            .then(totales => {
                setTotales(totales);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    }

    const fetchGridData = () => {
        setLoading(true);
        Utils.getData(`/moviles-inmovilizados/grid${getQueryString()}`)
            .then(inmovilizadosRes => {
                setInmovilizados(inmovilizadosRes.items);
                setCount(inmovilizadosRes.recordsTotal);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    }
    
    const getQueryString = () => {
        
        let query = `?page=${pagination.page}&perPage=${pagination.perPage}`;
        query += filters.tipoDeTicket ? `&tipoDeTicket=${filters.tipoDeTicket}` : "";
        query += filters.base ? `&base=${filters.base}` : "";
        query += filters.region ? `&region=${filters.region}` : "";
        query += filters.subregion ? `&subregion=${filters.subregion}` : "";
        query += filters.gerenciador ? `&gerenciador=${filters.gerenciador}` : "";
        query += filters.servicio ? `&servicio=${filters.servicio}` : "";
        query += filters.abono ? `&abono=${filters.abono}` : "";
        query += filters.marca ? `&marca=${filters.marca}` : "";
        query += filters.modelo ? `&modelo=${filters.modelo}` : "";
        query += filters.tipoVehiculo ? `&tipoVehiculo=${filters.tipoVehiculo}` : "";
        query += filters.funcion ? `&funcion=${filters.funcion}` : "";
        query += filters.anio ? `&anio=${filters.anio}` : "";
        query += filters.movilEstado ? `&movilEstado=${filters.movilEstado}` : "";
        // `fechaDesde` y `fechaHasta` NO tienen control en el panel, ni lo tenían antes: viajan
        // siempre vacías. Se dejan para que la forma del query no cambie; darles un control
        // sería funcionalidad nueva.
        query += filters.fechaDesde ? `&fechaDesde=${filters.fechaDesde}` : "";
        query += filters.fechaHasta ? `&fechaHasta=${filters.fechaHasta}` : "";
        // El backend valida la columna contra una lista blanca; lo que no esté ahí cae al orden
        // por defecto.
        query += `&sortColumn=${sort.campo}&sortDir=${sort.direccion}`;
        return query;
    }

    /**
     * Lo que el panel aplica al apretar "Buscar" (y al montar, por `applyOnMount`).
     *
     * La franja de resultados se muestra sólo si hay algún filtro cargado: con el query por
     * defecto —que acá es vacío— no tiene sentido ofrecer un "limpiar".
     */
    const handleFilterApply = (query) => {
        const conFiltros = Object.keys(query).some((k) => query[k] !== '' && query[k] !== null && query[k] !== undefined);
        setSearchActive(conFiltros);
        setFilters(Object.assign({}, FILTER_QUERY_INIT, query));
        setPagination((prev) => ({ ...prev, page: 0 }));
    }

    /**
     * La "✕" de la franja: devuelve los filtros al default y la oculta.
     *
     * NO toca el ordenamiento ni las columnas visibles: eso es del reset total. Ver "los dos
     * resets" en docs/design-system/grid-filter-panel.md.
     */
    const handleSearchClear = () => {
        setSearchActive(false);
        setFilters(Object.assign({}, FILTER_QUERY_INIT, getMovilesInmovilizadosFilterDefaults()));
        setPagination((prev) => ({ ...prev, page: 0 }));
        setFilterResetInputs(true);
    }

    const handlePageChange = (event, newPage) => {
        if (newPage < 0) return;
		setPagination({ ...pagination, page: newPage });
	}

	const handleRowsPerPageChange = (event) => {
		setPagination({ ...pagination, perPage: parseInt(event.target.value, 10), page: 0 });
	}

    /**
     * El botón "Restaurar" del toolbar: la vista completa, no sólo la búsqueda.
     *
     * Devuelve el ordenamiento y las columnas ocultas ADEMÁS de los filtros, y cierra el panel.
     * Reusa `handleSearchClear`, así el cambio de filtros es uno solo y el reset no dispara dos
     * consultas.
     *
     * En la fase 1 este botón no existía a propósito: sin orden ni selector de columnas habría
     * hecho exactamente lo mismo que la ✕. Con las fases 3 y 4 ya repone tres cosas distintas.
     */
    const handleFilterReset = () => {
        setSort({ campo: 'base', direccion: 'asc' });
        columnas.mostrarTodas();
        setDropdownActive(false);
        handleSearchClear();
    }

    const handleExport = () => {
        const url = `/moviles-inmovilizados/exportar-excel${getQueryString()}`;
        Utils.dataBlob(url, 'GET', [])
            .then(res => {
                const fileBlob = new File([res.data], res.filename)
                const fileUrl = window.URL.createObjectURL(fileBlob);
                const a = document.createElement('a');
                a.href = fileUrl;
                a.download = 'moviles_inmovilizados.xlsx';
                a.click();
                window.URL.revokeObjectURL(fileUrl);
            })
            .catch((err) => {
                console.error(err);
            });
    }

    return (
        <>
            {redirectTo && <Redirect push to={redirectTo} />}
            <MovilesInmovilizadosDashboard data={totales} loading={loading && totales.length === 0} />
            <div className="card">
                <div className="card-content">
                    <div className="card-body card-dashboard">
                        {/* Toolbar del patrón: TRES `col` de igual ancho —botones / franja de
                            resultados / botones de exportación—. Con `col-6 + col + col-6` se pasa
                            de 12 y el bloque de la derecha se va de línea.
                            `dt-icons` es lo que hace que el botón redondo tome los 55x55 de
                            `vec-datatables.css`, igual que en el resto de las grillas.

                            *** EL `mb-3` NO ES DECORATIVO. *** `.dt-icons` mide 30px de alto y los
                            botones redondos 55, así que sobresalen ~10px por abajo. Acá la fila, el
                            panel y la tabla son HERMANOS: con el panel cerrado su Collapse mide 0 y
                            la tabla arranca justo donde termina la fila, con los botones de
                            columnas y export encima del header. En Tickets no pasa porque la fila y
                            la tabla viven en contenedores distintos. */}
                        <div className="row dt-icons mb-2">
                            <div className="col">
                            <StyledTooltip
                                title={intl.formatMessage({
                                    id: 'moviles_inmovilizados.button.label_filtros',
                                    defaultMessage: 'Filtros',
                                })}
                                arrow
                                placement="right"
                            >
                                <div
                                    className="btn btn-grey-blue box-shadow-2 btn-round btn-lg btn-dt-main round-icon"
                                    onClick={() => setDropdownActive(!dropdownActive)}
                                >
                                    {dropdownActive ? <UpIcon /> : <ExpandMoreIcon />}
                                </div>
                            </StyledTooltip>
                            </div>
                            <div className="col">
                                {/* Franja compartida: commons/filters/GridSearchResultsBadge.jsx */}
                                <GridSearchResultsBadge
                                    visible={searchActive}
                                    mostrados={inmovilizados ? inmovilizados.length : 0}
                                    total={count}
                                    onReset={handleSearchClear}
                                />
                            </div>
                            <div className="col">
                                <div className="dt-buttons btn-group">
                                    <GridToolbarButton
                                        messageId="moviles_inmovilizados.tools_restore_filters.title"
                                        label="Restaurar filtros, columnas y ordenamiento"
                                        onClick={handleFilterReset}
                                    >
                                        <img className="filter-reset" src={LimpiarFiltros} alt="" />
                                    </GridToolbarButton>
                                    <GridColumnSelector
                                        columns={columnas.seleccionables}
                                        esVisible={columnas.esVisible}
                                        onToggle={columnas.toggle}
                                    />
                                    {/* Los tres botones del toolbar pasan por el mismo componente.
                                        `GridToolbarButton` arma el mismo markup que había acá a
                                        mano —`btn btn-secondary buttons-collection` con el icono
                                        adentro de un span— y además le pone el `aria-label`: un
                                        botón que sólo muestra un icono no tiene nombre accesible
                                        con el tooltip solo, porque el tooltip aparece al hover. */}
                                    <GridToolbarButton
                                        messageId="moviles_inmovilizados.tools_exportar"
                                        label="Exportar"
                                        icon="ft-upload"
                                        onClick={handleExport}
                                    />
                                </div>
                            </div>
                        </div>
                        <MovilesInmovilizadosGridFiltros
                            dropdownActive={dropdownActive}
                            filterResetInputs={filterResetInputs}
                            setFilterResetInputs={setFilterResetInputs}
                            onApply={handleFilterApply}
                            />
                        <TableContainer >
                            <Table>
                                <MovilesInmovilizadosTableHeader
                                    esVisible={columnas.esVisible}
                                    sort={sort}
                                    onSort={requestSort}
                                />
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            {/* Antes decía 11 con seis columnas. Ahora sigue a las visibles. */}
                                            <StyledTableCell colSpan={cantidadVisible} align="center">
                                                <FormattedMessage
                                                    id="Cargando"
                                                    defaultMessage="Cargando"
                                                />
                                            </StyledTableCell>
                                        </TableRow>
                                    ) : (
                                        inmovilizados && inmovilizados.length > 0 ?
                                        inmovilizados.map(inmovilizadosPorBase => (
                                            <MovilesInmovilizadosRow
                                                key={'movilesInmovilizadosGridRow_' + inmovilizadosPorBase.baseId}
                                                inmovilizadosPorBase={inmovilizadosPorBase}
                                                filters={filters || FILTER_QUERY_INIT}
                                                esVisible={columnas.esVisible}
                                                columnasVisibles={cantidadVisible}
                                            />
                                        )) : 
                                        <TableRow>
                                            {/* Antes decía 14 con seis columnas. */}
                                            <StyledTableCell colSpan={cantidadVisible} align="center">
                                                <FormattedMessage id="movilesInmovilizadosGrid.render.no_data_table" defaultMessage="No se encontraron resultados..." />
                                            </StyledTableCell>
                                        </TableRow>
                                        )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50, 100]}
                            component="div"
                            count={count}
                            rowsPerPage={pagination.perPage}
                            page={pagination.page}
                            onPageChange={handlePageChange}
                            onRowsPerPageChange={handleRowsPerPageChange}
                            labelRowsPerPage="Mostrar"
                            labelDisplayedRows={({ from, to, count }) => `Mostrando ${from} a ${to} de ${count}`}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

export default MovilesInmovilizadosGrid;